import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {emptyObject} from '@packages/utilities/fp/utils';
import {
  RELATIONSHIP_TYPES,
  type Interaction,
  type Timeline,
  type TargetDefinition,
  type FilterContext,
} from '@packages/systems/ix3/core';
import {Pair, pair} from '@packages/utilities/Pair';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import type {RenderTrackingContextItem} from '@packages/systems/element-tracking-types';

export type IX3ReifiedTargets = Record<string, Record<string, true>>;

/** JSON stringified version of `IX3ReifiedTargets` */
export type GetIx3ElementTargetsReturn = string & {
  __brand: 'IX3ElementTargets';
};

export const defaultStringifiedTargets = '{}' as GetIx3ElementTargetsReturn;

export const stringifyIx3ElementTargets = weakMemo(
  <i extends Interaction>(interactions: Array<i>) =>
    weakMemo(
      <t extends Timeline>(timelines: Array<t>): GetIx3ElementTargetsReturn => {
        const result: IX3ReifiedTargets = {};
        // No interaction to trigger timeline, so we can skip
        if (interactions.length === 0) return defaultStringifiedTargets;

        const addTarget = (target: TargetDefinition) => {
          const [key, value] = target;

          // filterBy only when related (match shape/existence walkers).
          if (target.length === 3) {
            const filterContext = target[2] as FilterContext;
            if (
              filterContext?.filterBy &&
              filterContext.relationship !== RELATIONSHIP_TYPES.NONE
            ) {
              addTarget(filterContext.filterBy as TargetDefinition);
            }
          }

          // Handle inst targets
          if (key !== IX3_WF_EXTENSION_KEYS.INST) return;
          if (!Array.isArray(value)) return;
          const hasScope = Array.isArray(value[0]);
          const nativeIdPath = hasScope ? value[0] : value;
          if (!Array.isArray(nativeIdPath)) return;

          const [componentId, elementId] = nativeIdPath;
          if (!componentId || !elementId) return;

          if (!result[componentId]) result[componentId] = {};
          result[componentId][elementId] = true;
        };

        interactions.forEach((interaction) => {
          // Soft-deleted records linger in the Designer store (and other
          // in-memory callers) with deleted=true. Skip them so the render
          // pipeline doesn't emit data-wf-target for orphan targets — that
          // attribute flips elements like Lottie into IX-controlled mode
          // and suppresses autoplay.
          if (interaction.deleted) return;

          // Component-scoped interactions still need component root IDs for
          // runtime scope checks, even when no explicit wf:inst targets exist.
          if (interaction.scope?.type === 'component') {
            const scopeComponentId = interaction.scope.componentId;
            if (scopeComponentId && !result[scopeComponentId]) {
              result[scopeComponentId] = {};
            }
          }

          interaction.triggers.forEach(([, config, target]) => {
            if (target) addTarget(target);
            // Scroll pin (tuple only)/endTrigger/scroller — same as collectTriggerTargets.
            const scroll = config?.scrollTriggerConfig;
            if (!scroll) return;
            if (Array.isArray(scroll.pin)) addTarget(scroll.pin);
            if (scroll.endTrigger) addTarget(scroll.endTrigger);
            if (scroll.scroller) addTarget(scroll.scroller);
          });
        });

        timelines.forEach((timeline) => {
          if (timeline.deleted) return;
          timeline.actions.forEach((action) => {
            action.targets.forEach(addTarget);
          });
        });

        return JSON.stringify(result) as GetIx3ElementTargetsReturn;
      }
    )
);

export const getIx3ElementTargets = singleMemo(
  (stringifiedTargets: GetIx3ElementTargetsReturn): IX3ReifiedTargets => {
    try {
      return JSON.parse(stringifiedTargets) as IX3ReifiedTargets;
    } catch (error) {
      return emptyObject;
    }
  }
);

export const IX3_TARGET_ATTRIBUTE_PREFIX = 'data-wf-target';

export const elementTargetAttribute = (
  ids: Array<Pair<Pair<string, string>, Array<string>>>
) => pair(IX3_TARGET_ATTRIBUTE_PREFIX, JSON.stringify(ids));

export const COMPONENT_ID_ATTR = 'data-wf-component-id';
export const VARIANT_STATE_ATTR = 'data-wf-variant-state';

/**
 * Returns the componentId to set as data-wf-component-id on a component root
 * when that component is targeted by at least one IX3 interaction; otherwise null.
 */
export function getComponentRootIdForAttribute(
  trackingContext: RenderTrackingContextItem[],
  pageId: string,
  ix3ReifiedTargets: IX3ReifiedTargets
): string | null {
  for (let i = trackingContext.length - 1; i >= 0; i--) {
    const item = trackingContext[i];
    if (item?.type === 'component' && item.componentId) {
      if (item.componentId === pageId) {
        return null;
      }

      if (ix3ReifiedTargets[item.componentId]) {
        return item.componentId;
      }

      const instanceIdMatch = item.instanceId
        ? Object.values(ix3ReifiedTargets).some((targets) =>
            Boolean(targets[item.instanceId as string])
          )
        : false;

      if (instanceIdMatch) {
        return item.componentId;
      }

      return null;
    }
  }
  return null;
}
